import moment from 'moment';

export const getCurrentUserTz = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Minutes east of UTC for an IANA timezone at a given instant (DST-aware).
 * `moment` has no IANA timezone database of its own (that's `moment-timezone`,
 * which isn't installed) — `Intl.DateTimeFormat` is the only way to resolve an
 * IANA zone name to its offset, so it's used here purely to compute that
 * offset. The actual formatting below goes through `moment`.
 */
const getUtcOffsetMinutes = (
  timeZone: string,
  date: string | number | Date,
): number => {
  const instant = new Date(date);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);
  const get = (type: string) =>
    Number(parts.find((part) => part.type === type)?.value);
  const wallClockAsUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );

  return Math.round((wallClockAsUtc - instant.getTime()) / 60000);
};

/** Formats a date with a given moment format string in the given IANA timezone, falling back to the browser's timezone. */
export const formatDateInTimezone = (
  date: string | number | Date,
  timeZone: string | undefined,
  format: string,
): string => {
  const momentDate = moment(date);

  // Intl.DateTimeFormat throws a RangeError on an invalid date (unlike
  // moment, which just renders "Invalid date") — guard before it ever gets
  // there so a malformed date degrades the same way it always has.
  if (!momentDate.isValid()) {
    return momentDate.format(format);
  }

  const offsetMinutes = getUtcOffsetMinutes(
    timeZone || getCurrentUserTz(),
    date,
  );

  return momentDate.utcOffset(offsetMinutes).format(format);
};

/** Formats a date as "D MMM" (e.g. "25 Mar") in the given IANA timezone, falling back to the browser's timezone. */
export const formatShortDateInTimezone = (
  date: string | number | Date,
  timeZone?: string,
): string => formatDateInTimezone(date, timeZone, 'D MMM');
